/**
 * DEC-001: Convert FormViewConfig to TabStructure
 * 
 * Converts custom layout definitions (FormViewConfig.sections) into
 * TabStructure[] format used by TabRenderer.
 * 
 * This enables Experience Views to render custom layouts instead of
 * falling back to pure meta-driven layouts.
 * 
 * Automatically includes missing fields from meta in a catch-all section.
 */

import type { TabStructure, SectionStructure, ColumnStructure } from '@/features/inventory/item-detail/utils/buildTabsFromMeta';
import type { DocTypeSchema, DocFieldSchema } from '@/lib/erp/meta';
import type { FormViewConfig, SectionConfig } from '@/lib/views/types';
import { extractFieldsFromSystemTab } from './extractFieldsFromSystemTabs';
import { computeUnmappedFields } from '@/lib/views/utils';
import { buildMetaFieldGroupIndex } from './deriveFieldGroups';

/**
 * Find field in meta by fieldname
 */
function findFieldInMeta(
  fieldname: string,
  meta: DocTypeSchema
): DocFieldSchema | undefined {
  return meta.fields.find(f => f.fieldname === fieldname);
}

/**
 * Create a new tab (helper function mirroring buildTabsFromMeta pattern)
 */
function createTab(): TabStructure {
  return {
    name: "__details",
    label: "Details",
    sections: [],
  };
}

/**
 * Create a new section (helper function mirroring buildTabsFromMeta pattern)
 */
function createSection(sectionConfig: SectionConfig): SectionStructure {
  return {
    name: sectionConfig.id,
    label: sectionConfig.title || sectionConfig.id,
    columns: [],
  };
}

/**
 * Create a new column (helper function mirroring buildTabsFromMeta pattern)
 */
function createColumn(name?: string): ColumnStructure {
  return {
    name: name || "",
    label: "",
    fields: [],
  };
}

/**
 * Convert FormViewConfig to TabStructure[]
 * 
 * Maps custom layout definitions to the TabStructure format used by TabRenderer.
 * 
 * Rules:
 * - Single tab ("__details") containing all sections
 * - Each FormViewConfig.sections[i] → one SectionStructure
 * - Each LayoutRow → columns in that section
 * - Each LayoutField → one column containing one DocFieldSchema
 * - Hidden fields are excluded from layout but still count as mapped
 * - Missing fields log warnings in dev mode and are skipped
 * - Dynamic sections (_dynamicFromSystemTab) are populated from SystemView tabs
 * 
 * @param formView - FormViewConfig from DocTypeView
 * @param meta - DocTypeSchema from ERP meta API
 * @param formData - Form data for conditional visibility checks
 * @returns TabStructure[] array (single tab for now)
 */
export function convertFormViewToTabStructure(
  formView: FormViewConfig,
  meta: DocTypeSchema,
  _formData?: Record<string, any>
): TabStructure[] {
  // Create single tab
  const tab = createTab();
  
  // Process each section
  for (const sectionConfig of formView.sections) {
    // Note: Conditional visibility is checked at the tab level in ExperienceView,
    // not here, so sections with conditional visibility are still included
    // in the tab structure but will be filtered when creating custom tabs
    
    const sectionStruct = createSection(sectionConfig);
    
    // Handle dynamic sections (populated from SystemView tabs)
    if (sectionConfig._dynamicFromSystemTab) {
      const systemTabName = sectionConfig._dynamicFromSystemTab;
      const fields = extractFieldsFromSystemTab(meta, systemTabName);
      
      if (fields.length > 0) {
        // Create columns from fields (group into rows of 3)
        const fieldsPerRow = 3;
        for (let i = 0; i < fields.length; i += fieldsPerRow) {
          const rowFields = fields.slice(i, i + fieldsPerRow);
          const column = createColumn();
          column.fields = rowFields;
          sectionStruct.columns.push(column);
        }
      }
    } else {
      // Process each layout row in the section
      for (const row of sectionConfig.layout) {
        // Process each column (LayoutField) in the row
        for (const layoutField of row.columns) {
          // Skip hidden fields (exclude from layout, but still mapped)
          if (layoutField.visibility === 'hidden') {
            continue;
          }
          
          // Find field in meta, or use virtual field definition
          let field = findFieldInMeta(layoutField.fieldname, meta);

          if (!field && layoutField.virtualFieldDef) {
            // Synthetic field for virtual/computed toggles (e.g., included_in_print_rate)
            field = {
              fieldname: layoutField.fieldname,
              fieldtype: layoutField.virtualFieldDef.fieldtype,
              label: layoutField.virtualFieldDef.label,
              description: layoutField.virtualFieldDef.description,
            } as DocFieldSchema;
          }

          if (!field) {
            // Field not found - skip
            continue;
          }
          
          // Create column with single field
          // Use fieldname as column name to ensure unique keys in React
          const column = createColumn(layoutField.fieldname);
          column.fields = [field];
          sectionStruct.columns.push(column);
        }
      }
    }
    
    // Only add section if it has columns with fields
    if (sectionStruct.columns.length > 0) {
      tab.sections.push(sectionStruct);
    }
  }
  
  // Remove empty sections (safety check)
  tab.sections = tab.sections.filter(
    (section) => section.columns.some((col) => col.fields.length > 0)
  );
  
  // Automatically add missing fields from meta, grouped by their original meta sections
  const { unmappedFieldnames } = computeUnmappedFields(meta, formView);
  
  
  if (unmappedFieldnames.length > 0) {
    const unmappedFields = meta.fields.filter(f => 
      unmappedFieldnames.includes(f.fieldname) &&
      f.hidden !== 1 &&
      f.fieldtype !== 'Table' && // Exclude child tables
      f.fieldtype !== 'Tab Break' &&
      f.fieldtype !== 'Section Break' &&
      f.fieldtype !== 'Column Break' &&
      !f.fieldname.startsWith('__') // Exclude internal fields
    );
    
    
    if (unmappedFields.length > 0) {
      // Build meta group index to understand which section each field belongs to
      const metaGroupIndex = buildMetaFieldGroupIndex(meta.fields, {
        defaultGroupLabel: 'General',
      });
      
      // Group unmapped fields by their meta section
      const fieldsBySection = new Map<string, { label: string; fields: DocFieldSchema[] }>();
      
      for (const field of unmappedFields) {
        const groupInfo = metaGroupIndex.get(field.fieldname);
        const sectionKey = groupInfo?.key || 'fallback-general';
        const sectionLabel = groupInfo?.label || 'General';
        
        if (!fieldsBySection.has(sectionKey)) {
          fieldsBySection.set(sectionKey, {
            label: sectionLabel,
            fields: [],
          });
        }
        
        fieldsBySection.get(sectionKey)!.fields.push(field);
      }
      
      // Create sections for each group, preserving meta order
      // The Map preserves insertion order, and fields are already in meta order
      // So we can iterate directly over the entries
      const sectionEntries = Array.from(fieldsBySection.entries());
      
      // Sort by the original order in meta (sectionKey contains tab and section indices)
      sectionEntries.sort((a, b) => {
        const keyA = a[0];
        const keyB = b[0];
        // Extract tab and section indices from keys (format: t{tabIndex}-s{sectionIndex}-...)
        const matchA = keyA.match(/^t(\d+)-s(\d+)/);
        const matchB = keyB.match(/^t(\d+)-s(\d+)/);
        
        if (matchA && matchB) {
          const tabA = parseInt(matchA[1], 10);
          const tabB = parseInt(matchB[1], 10);
          const secA = parseInt(matchA[2], 10);
          const secB = parseInt(matchB[2], 10);
          
          // First sort by tab, then by section
          if (tabA !== tabB) return tabA - tabB;
          return secA - secB;
        }
        
        // Fallback: maintain original order
        return 0;
      });
      
      for (const [_sectionKey, { label, fields }] of sectionEntries) {
        // Generate a unique section name from the section label
        // Sanitize the label to create a valid section name
        const sanitizeSectionName = (text: string): string => {
          return text
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
            .replace(/\s+/g, '-') // Replace spaces with hyphens
            .replace(/-+/g, '-') // Collapse multiple hyphens
            .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
        };
        
        // Improve label handling: if label looks like a fieldname (section_break_15), use a better fallback
        let displayLabel = label || 'More Information';
        if (displayLabel.startsWith('section_break_') || displayLabel.match(/^[a-z_]+_\d+$/)) {
          // This is a technical fieldname, use a more user-friendly label
          // Try to infer a better label from the fields in this section
          const fieldTypes = fields.map(f => f.fieldtype);
          const hasDates = fieldTypes.some(t => t === 'Date' || t === 'Datetime');
          const hasLinks = fieldTypes.some(t => t === 'Link');
          const hasNumbers = fieldTypes.some(t => t === 'Currency' || t === 'Float' || t === 'Int');
          
          if (hasDates && hasLinks) {
            displayLabel = 'Dates & References';
          } else if (hasDates) {
            displayLabel = 'Dates';
          } else if (hasLinks) {
            displayLabel = 'References';
          } else if (hasNumbers) {
            displayLabel = 'Additional Details';
          } else {
            displayLabel = 'Additional Information';
          }
        }
        
        const baseName = sanitizeSectionName(displayLabel);
        const sectionName = `more-info-${baseName}`;
        
        
        // Check if a section with this name already exists
        let section = tab.sections.find(s => s.name === sectionName);
        
        if (!section) {
          section = {
            name: sectionName,
            label: displayLabel,
            columns: [],
          };
          tab.sections.push(section);
        }
        
        // Group fields into columns (3 fields per column for better layout)
        const fieldsPerColumn = 3;
        for (let i = 0; i < fields.length; i += fieldsPerColumn) {
          const columnFields = fields.slice(i, i + fieldsPerColumn);
          const column = createColumn();
          column.fields = columnFields;
          section.columns.push(column);
        }
      }
    }
  }
  
  // Final filter to remove any empty sections (including newly created ones)
  tab.sections = tab.sections.filter(
    (section) => section.columns.some((col) => col.fields.length > 0)
  );
  
  // Return single tab in array (matching TabStructure[] return type)
  return tab.sections.length > 0 ? [tab] : [];
}

